/**
 * TradeInReportModal
 *
 * Single-location modal for the Trade-In Report (Reports → Service). Shows the
 * used vehicles taken in on trade for the selected location + date range so
 * Service can create recon repair orders. The backend exposes ONLY whitelisted
 * trade fields — no deal gross ever reaches this component.
 *
 * Mirrors the TsmItemsReportModal shell (status states, retry-on-network-error,
 * print layout, ReportExportButtons footer) but is single-location and uses the
 * shared date-range selector (defaults to month-to-date).
 */

import React, { useState } from 'react';
import { XMarkIcon, PlayIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { ReportDateRangeSelector, getDefaultStartDate, getDefaultEndDate } from './ReportDateRangeSelector';
import { printCurrentPage } from '../../utils/reportExport';
import { loadExcelJS } from '../../utils/loadExcelJS';
import { runReport, RunReportResponse } from '../../services/api';
import { TradeInReport, TradeInReportData } from './renderers/TradeInReport';

interface TradeInReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const TradeInReportModal: React.FC<TradeInReportModalProps> = ({
  report,
  isOpen,
  onClose,
  locationId,
  locationName,
}) => {
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  const isValidDateRange = !!startDate && !!endDate && startDate <= endDate;

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    if (!isValidDateRange) return;

    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Reading trade-in data...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runReport(report.id, locationId, startDate, endDate);

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Trade-In Report attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  const handlePrintPDF = () => {
    printCurrentPage();
  };

  const handleExportExcel = async () => {
    if (!reportData?.data) return;
    const data = reportData.data as TradeInReportData;

    const ExcelJS = await loadExcelJS();
    const wb = new ExcelJS.Workbook();
    wb.creator = "Darling's Dashboard";
    wb.created = new Date();

    const ws = wb.addWorksheet('Trade-Ins', {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 5 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const HEADERS = ['Date', 'Sales Mgr', 'Trade', 'Stock #', 'Year', 'Make', 'Model', 'Miles', 'Plan', 'Comments'];
    const totalCols = HEADERS.length;
    const lastColLetter = ws.getColumn(totalCols).letter;

    // Title rows
    ws.getCell('A1').value = `Trade-In Report — ${locationName}`;
    ws.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    ws.mergeCells(`A1:${lastColLetter}1`);
    ws.getCell('A2').value = `${startDate} to ${endDate} · ${data.totalTrades} trade${data.totalTrades === 1 ? '' : 's'}`;
    ws.getCell('A2').font = { size: 11, color: { argb: 'FF4B5563' } };
    ws.mergeCells(`A2:${lastColLetter}2`);
    ws.getCell('A3').value = `Generated: ${new Date().toLocaleString('en-US')}`;
    ws.getCell('A3').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells(`A3:${lastColLetter}3`);

    // Header row (row 5)
    const headerRow = ws.getRow(5);
    HEADERS.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
      cell.alignment = { horizontal: i === 4 || i === 7 ? 'right' : i === 2 ? 'center' : 'left', vertical: 'middle' };
    });
    headerRow.height = 20;

    // Data rows from row 6
    data.rows.forEach((row, i) => {
      const r = ws.getRow(6 + i);
      r.values = [
        row.date,
        row.salesManager ?? '',
        `T${row.tradeNumber}`,
        row.stockNumber ?? '',
        row.year ?? '',
        row.make ?? '',
        row.model ?? '',
        row.miles ?? '',
        row.planLabel ? `${row.plan} — ${row.planLabel}` : (row.plan ?? ''),
        row.comment ?? '',
      ];
      r.getCell(3).alignment = { horizontal: 'center' };
      r.getCell(5).alignment = { horizontal: 'right' };
      r.getCell(8).alignment = { horizontal: 'right' };
      r.getCell(8).numFmt = '#,##0';
      if (i % 2 === 1) {
        for (let c = 1; c <= totalCols; c++) {
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };
        }
      }
    });

    if (data.rows.length > 0) {
      ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5 + data.rows.length, column: totalCols } };
    }

    // Column widths
    ws.getColumn(1).width = 12;
    ws.getColumn(2).width = 18;
    ws.getColumn(3).width = 7;
    ws.getColumn(4).width = 12;
    ws.getColumn(5).width = 7;
    ws.getColumn(6).width = 14;
    ws.getColumn(7).width = 16;
    ws.getColumn(8).width = 10;
    ws.getColumn(9).width = 26;
    ws.getColumn(10).width = 40;

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeLoc = locationName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');
    link.download = `Trade-In-Report-${safeLoc}-${startDate}_${endDate}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static print:block"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none print:block print:overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">{locationName} • Service</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible print:flex-none">
          <div className="mb-6 print:hidden">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Report Period</h3>
            <ReportDateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              disabled={status === 'running'}
            />
          </div>

          <div className="mb-6 print:hidden">
            <button
              onClick={handleRunReport}
              disabled={!isValidDateRange || status === 'running'}
              className={`
                inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${!isValidDateRange || status === 'running'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
                }
              `}
            >
              {status === 'running' ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {runningMessage}
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Run Report
                </>
              )}
            </button>
          </div>

          <div className="border-t border-gray-200 mb-6 print:hidden" />

          <div id="report-print-content">
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">Trade-In Report</h1>
              <p className="text-sm text-gray-600">
                {locationName} • Service • {startDate} to {endDate}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <WrenchScrewdriverIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Select a date range and click "Run Report" to list trade-ins for {locationName}.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <TradeInReport data={reportData.data as TradeInReportData} />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
